import * as React from "react";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { EventsDefinitions } from "@graylog/server-api";
import { FormGroup } from "components/bootstrap";
import { InputDescription, Select } from "components/common";
import useDebouncedValue from "hooks/useDebouncedValue";
const normalizeTag = (raw) => raw.trim().toLowerCase();
const MAX_TAGS = 64;
const MAX_TAG_LENGTH = 128;
const VALID_TAG_PATTERN = /^[a-z0-9_.-]+$/;
const SUGGESTION_LIMIT = 50;
const DEBOUNCE_MS = 300;
const VALUE_DELIMITER = "";
const HELP_TEXT = "Press Enter or Tab to add. Tags are lowercased and deduplicated. Only lowercase letters, digits, hyphens, underscores, and dots are allowed.";
const isTooLong = (tag) => tag.length > MAX_TAG_LENGTH;
const hasInvalidChars = (tag) => !VALID_TAG_PATTERN.test(tag);
const quote = (s) => `"${s}"`;
const buildTooLongMessage = (tagsList) => {
  if (tagsList.length === 0) return null;
  const isPlural = tagsList.length > 1;
  return `Tag${isPlural ? "s" : ""} ${tagsList.map(quote).join(", ")} ${isPlural ? "exceed" : "exceeds"} the maximum length of ${MAX_TAG_LENGTH} characters.`;
};
const buildInvalidCharsMessage = (tagsList) => {
  if (tagsList.length === 0) return null;
  const isPlural = tagsList.length > 1;
  return `Tag${isPlural ? "s" : ""} ${tagsList.map(quote).join(", ")} ${isPlural ? "contain" : "contains"} invalid characters. Only lowercase letters, digits, hyphens, underscores, and dots are allowed.`;
};
const TagsEditor = ({ tags, onChange, disabled = false, error = null }) => {
  var _a;
  const [input, setInput] = useState("");
  const [duplicateAttempt, setDuplicateAttempt] = useState(null);
  const [debouncedInput] = useDebouncedValue(input, DEBOUNCE_MS);
  const { data, isFetching } = useQuery({
    queryKey: ["event-definitions", "tag-suggestions", debouncedInput],
    queryFn: () => EventsDefinitions.suggestTags(debouncedInput, SUGGESTION_LIMIT),
    placeholderData: keepPreviousData,
    staleTime: 3e4,
    enabled: !disabled
  });
  const suggestions = ((_a = data == null ? void 0 : data.tags) != null ? _a : []).filter((s) => !tags.includes(s)).map((value) => ({ value, label: value }));
  const handleChange = (joined) => {
    const raw = joined ? joined.split(VALUE_DELIMITER) : [];
    const normalized = raw.map(normalizeTag).filter((value) => value.length > 0);
    const deduped = Array.from(new Set(normalized));
    onChange(deduped.slice(0, MAX_TAGS));
    setInput("");
    setDuplicateAttempt(null);
  };
  const flagDuplicateAttempt = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const normalized = normalizeTag(trimmed);
    if (tags.includes(normalized)) {
      setDuplicateAttempt(normalized);
    }
  };
  const tooLongTags = tags.filter(isTooLong);
  const invalidCharTags = tags.filter((tag) => !isTooLong(tag) && hasInvalidChars(tag));
  const messages = [
    buildTooLongMessage(tooLongTags),
    buildInvalidCharsMessage(invalidCharTags),
    duplicateAttempt ? `Tag "${duplicateAttempt}" has already been added.` : null
  ].filter((m) => m !== null);
  const localValidationError = messages.length === 0 ? null : /* @__PURE__ */ React.createElement(React.Fragment, null, messages.map((m) => /* @__PURE__ */ React.createElement("div", { key: m }, m)));
  const combinedError = error != null ? error : localValidationError;
  return /* @__PURE__ */ React.createElement(FormGroup, { controlId: "event-definition-tags", validationState: combinedError ? "error" : null }, /* @__PURE__ */ React.createElement(
    Select,
    {
      ...{
        inputValue: input,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === "Tab") {
            flagDuplicateAttempt();
          }
        }
      },
      inputId: "event-definition-tags",
      "aria-label": "Event Definition Tags",
      multi: true,
      allowCreate: true,
      delimiter: VALUE_DELIMITER,
      options: suggestions,
      value: tags.join(VALUE_DELIMITER),
      onChange: handleChange,
      onInputChange: (value, actionMeta) => {
        if ((actionMeta == null ? void 0 : actionMeta.action) === "input-change") {
          setInput(value);
          setDuplicateAttempt(null);
        }
      },
      onBlur: flagDuplicateAttempt,
      isLoading: isFetching,
      disabled,
      placeholder: "e.g. authentication, brute-force, compliance"
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { error: combinedError, help: HELP_TEXT }));
};
export default TagsEditor;
